<template>
    <div id="app">
        <template v-if="onEducatorHomepage">
            <TopHat source="" />
        </template>
        <div ref="productBarContainer">
            <NebulaProductBar
                :prelogin="hideAvatar"
                class="product-bar"
                :homeLogoElement="homeLogoElementValue"
                :homeLink="homeLinkValue"
                searchAction=""
                @open-global-nav="handleToggleGlobalNav"
                @open-profile-panel="openProfilePopover"
                :globalNavLabel="$t('Menu')"
                pageTitleLogo="career-connect"
                :profileButtonLabel="$t('View your Profile')"
                :userInitials="initials"
                :avatarImage="avatarImage"
                :hideGlobalNav="isBlockedRole"
            >
                <template v-slot:actionButton>
                    <NebulaButton
                        v-if="showHowItWorksButton"
                        icon-left="information"
                        :text="$t('How It Works')"
                        size="s"
                        type="flat"
                        router
                        link="/how-it-works"
                        data-click-type="Product Bar"
                        data-click-name="How it Works"
                    />
                    <NotificationsPopover v-if="showNotifications" />
                </template>
            </NebulaProductBar>
            <AccountPopover
                v-if="profileButtonReady"
                ref="accountPopover"
                :member="userData"
                :organization="organization"
                :loading="loading"
                id="popover-accounts"
            />
        </div>

        <!-- Global Error Banner -->
        <div class="error-banner-wrapper" :class="{ 'is-open': bannerError }">
            <div class="error-banner-wrapper__inner">
                <div v-if="displayedBannerError" class="error-banner-container">
                    <ErrorBanner
                        :header="displayedBannerError?.title"
                        :description="displayedBannerError?.message"
                        :color="displayedBannerError?.color"
                        :actionTitle="displayedBannerError?.actionTitle"
                        :actionTrigger="displayedBannerError?.actionCallback"
                        dismissible
                        @close="clearBannerError"
                    />
                </div>
            </div>
        </div>

        <div class="product-container">
            <GlobalNav
                v-if="!isBlockedRole"
                homeLink="/"
                :data="globalNavData"
                :isOpen="globalNavOpen"
                ref="globalNav"
                @toggleGlobalNav="handleToggleGlobalNav"
            />
            <button
                v-if="globalNavOpen && !isBlockedRole"
                class="careers-global-nav__overlay"
                @click="handleToggleGlobalNav"
                aria-label="Close navigation menu"
                type="button"
            />
            <div class="product-content">
                <GlobalPageSpinner :isLoading="loading" />
                <div :aria-busy="loading">
                    <router-view v-show="!loading" />
                </div>
            </div>
        </div>
        <NebulaFooter v-if="!auth.loading" :user-is-student="false" class="careers-footer" :footerLinks="footerLinks" backToTop />

        <UserManagementModals />
        <ProfileSetupModals />
        <RequestModals />
        <RequestTemplateModals />
        <WelcomeModal v-if="welcomeModalsEnabled" />
        <PresentExamplesModal />
    </div>
</template>

<script>
import { NebulaFooter, NebulaButton, NebulaProductBar } from '@discoveryedu/nebula-components';
import AccountPopover from '@/components/shared/AccountPopover.vue';
import UserManagementModals from '@/components/modals/UserManagementModals.vue';
import ProfileSetupModals from '@/components/modals/ProfileSetupModals.vue';
import RequestModals from '@/components/modals/RequestModals.vue';
import WelcomeModal from '@/components/modals/home/WelcomeModal/WelcomeModal.vue';
import PresentExamplesModal from '@/components/modals/PresentExamplesModal.vue';
import useWelcomeModalStore from '@/stores/useWelcomeModalStore';
import ErrorBanner from '@/components/shared/ErrorBanner.vue';

import GlobalPageSpinner from '@/views/GlobalPageSpinner.vue';

import GlobalNav from '@/components/shared/GlobalNav.vue';
import analytics from '@/analytics';
import pendo from '@/pendo';
import { useErrorReporting } from '@/composables/useErrorReporting';

import { educatorRoles, MEMBER_ROLES, BLOCKED_ROLES, envMap, ATLAS_APP_BASE_URL } from '@/constants';
import { ACCESS_STATES } from '@/utils/auth/accessState';
import { getFooterLinks } from '@/data/footerLinks';
import createPayload from '@/mixins/data/createPayload';
import NotificationsPopover from '@/components/shared/NotificationsPopover.vue';
import RequestTemplateModals from '@/components/modals/RequestTemplateModals.vue';
import { useMainStore } from '@/stores/useMainStore';
import { useAppStore } from '@/stores/useAppStore';
import { useEmployeeStore } from '@/stores/useEmployeeStore';
import { useNotificationStore } from '@/stores/useNotificationStore';
import { useOrganizationStore } from '@/stores/useOrganizationStore';
import { useAuth0 } from '@/auth/index';
import { useDeAuth } from '@/auth/deAuth';
import TopHat from './components/dashboard/Educator/TopHat.vue';

export default {
    name: 'App',
    components: {
        AccountPopover,
        NotificationsPopover,
        NebulaFooter,
        NebulaButton,
        NebulaProductBar,
        GlobalNav,
        UserManagementModals,
        ProfileSetupModals,
        RequestModals,
        RequestTemplateModals,
        WelcomeModal,
        PresentExamplesModal,
        GlobalPageSpinner,
        ErrorBanner,
        TopHat,
    },
    setup() {
        const store = useMainStore();
        const appStore = useAppStore();
        const employeeStore = useEmployeeStore();
        const notificationStore = useNotificationStore();
        const orgStore = useOrganizationStore();
        const auth = useAuth0();
        const de = useDeAuth();
        const { clearBannerError } = useErrorReporting();
        return {
            store,
            appStore,
            employeeStore,
            notificationStore,
            orgStore,
            auth,
            de,
            clearBannerError,
        };
    },
    mixins: [createPayload],
    data() {
        return {
            profilePanelCollapsed: true,
            globalNavOpen: false,
            footerLinks: getFooterLinks(),
            favoritesLoaded: false,
            profileButtonReady: false,
            welcomeModalChecked: false,
            analyticsInitialized: false,
            pendoInitialized: false,
            displayedBannerError: null,
            bannerErrorClearTimeout: null,
        };
    },
    computed: {
        showHowItWorksButton() {
            const hiddenOn = ['how-it-works', 'profile-setup'];
            return !hiddenOn.includes(this.$route.name) && (this.appStore.isVolunteer || this.appStore.isEducator);
        },
        appMode() {
            return this.appStore.appMode;
        },
        authenticated() {
            return this.auth.isAuthenticated || this.de.isAuthenticated;
        },
        hideAvatar() {
            return !this.authenticated || this.loading || this.setupMode;
        },
        shouldShowMessagesLink() {
            return !this.appStore.isDeAdmin && this.authenticated && MEMBER_ROLES.includes(this.appMode);
        },
        showNotifications() {
            if (!this.authenticated || !MEMBER_ROLES.includes(this.appMode)) {
                return false;
            }

            return !this.hideAvatar;
        },
        setupMode() {
            return this.appStore.accessState === ACCESS_STATES.ONBOARDING;
        },
        getSidebarEl() {
            return document.getElementsByClassName('nebula-sidebar')[0];
        },
        userData() {
            // For educators, data is in mainStore.user.data
            // For employees, data is in employeeStore.user.data
            if (educatorRoles.includes(this.appMode)) {
                return this.store.user.data;
            }
            return this.employeeStore.user.data;
        },
        organization() {
            return this.orgStore.organization;
        },
        loading() {
            return this.store.status.app.loading;
        },
        initials() {
            const { organization, userData } = this;
            if (this.appStore.isOrgAdmin) {
                return organization?.name?.charAt(0) || '';
            }
            // Check for both null/undefined AND empty objects
            if (!userData || Object.keys(userData).length === 0) {
                return '';
            }
            const firstInitialPreferred = userData.preferredName && userData.preferredName.charAt(0);
            const firstInitial = userData.firstName && userData.firstName.charAt(0);
            const lastInitial = userData.lastName && userData.lastName.charAt(0);

            if (!firstInitial || !lastInitial) {
                return '';
            }
            const compiledInitals = `${firstInitialPreferred || firstInitial}${lastInitial}`;
            return compiledInitals;
        },
        avatarImage() {
            const { organization, userData } = this;
            if (this.appStore.isOrgAdmin) {
                return organization?.avatarUrl;
            }
            return userData?.avatarUrl;
        },
        helpAndSupportLink() {
            if (educatorRoles.includes(this.appMode)) {
                return 'https://help.discoveryeducation.com/hc/en-us/articles/20612359490323-Getting-Started-with-Career-Connect-Educators';
            }

            return 'https://help.discoveryeducation.com/hc/en-us/articles/20623107444371-Getting-Started-with-Career-Connect-Connectors';
        },
        onEducatorHomepage() {
            return this.$route.name === 'dashboard' && this.appStore.isEducator;
        },
        isBlockedRole() {
            return BLOCKED_ROLES.includes(this.appMode);
        },
        isDevQaOrStage() {
            const env = import.meta.env.MODE;
            return ['development', 'qa', 'staging'].includes(env);
        },
        homeLogoElementValue() {
            return this.appStore.isEducator && this.isDevQaOrStage ? 'a' : 'RouterLink';
        },
        homeLinkValue() {
            if (this.appStore.isEducator && this.isDevQaOrStage) {
                const env = import.meta.env.MODE;
                const envPrefix = envMap[env.toLowerCase()] || '';
                return `https://app.${envPrefix}discoveryeducation.com/`;
            }
            return '/';
        },
        nonEducatorNavLinks() {
            const links = [];
            if (!this.appStore.isDeAdmin) {
                links.push({
                    display: 'Dashboard',
                    icon: 'dashboard',
                    path: '/',
                });
            }

            // Group Admins should only see the Dashboard in the Hamburger Menu
            if (this.appStore.isGroupAdmin) return links;

            if (!this.appStore.isDeAdmin) {
                links.push({
                    display: 'Request Hub',
                    icon: 'clipboard',
                    path: '/connect-directory',
                });
            }
            if (this.appStore.isVolunteer || this.appStore.isEducator) {
                links.push({
                    display: 'How it Works',
                    icon: 'information',
                    path: '/how-it-works',
                });
            }
            if (this.shouldShowMessagesLink) {
                links.push({
                    display: 'Messages',
                    icon: 'mail',
                    path: '/message',
                });
            }

            if (this.appStore.isDeAdmin) {
                links.push(
                    {
                        display: 'Educator Management',
                        icon: 'explore',
                        path: '/educator-management',
                    },
                    {
                        display: 'Organization Management',
                        icon: 'explore',
                        path: '/org-management',
                    },
                    {
                        display: 'Requests Admin',
                        icon: 'explore',
                        path: '/request-admin',
                    },
                    {
                        display: 'Test Atlas Asset',
                        icon: 'explore',
                        path: '/test-asset',
                    },
                    {
                        display: 'Feature Flags',
                        icon: 'explore',
                        path: '/feature-flags',
                    },
                    {
                        display: 'Request Templates',
                        icon: 'explore',
                        path: '/request-templates',
                    },
                );

                links.push({
                    display: 'Group Management',
                    icon: 'hierarchy',
                    path: '/groups',
                });
            }
            return links;
        },
        supportLinks() {
            const links = [];
            if (MEMBER_ROLES.includes(this.appMode)) {
                links.push({
                    display: 'Settings and Preferences',
                    icon: 'gear',
                    path: '/settings',
                });
            }
            links.push({
                display: this.$t('Help & Support'),
                icon: 'help',
                path: this.helpAndSupportLink,
            });
            return links;
        },
        nonEducatorAccountLinks() {
            if (this.appStore.isGroupAdmin) return [];
            const links = [];
            const employeeId = this.userData?.employeeId;

            if (employeeId) {
                links.push({
                    display: this.$t('My Profile'),
                    icon: 'user',
                    path: `/volunteer/${employeeId}`,
                });
            }
            links.push({
                display: this.$t('Settings and Preferences'),
                icon: 'gear',
                path: '/settings',
            });
            if (this.organization) {
                const { organizationId } = this.organization;
                const orgPath = `${ATLAS_APP_BASE_URL}/org-${organizationId}`;
                links.push({
                    display: this.$t('My Organization'),
                    icon: 'buildings',
                    path: orgPath,
                });
            }
            return links;
        },
        nonEducatorNavData() {
            const sections = [
                { name: 'My Career App', links: this.nonEducatorNavLinks },
                { name: 'Support', links: [{ display: this.$t('Help & Support'), icon: 'help', path: this.helpAndSupportLink }] },
            ];

            const accountLinks = this.nonEducatorAccountLinks;
            if (accountLinks.length) {
                sections.splice(1, 0, { name: 'My Account', links: accountLinks });
            }
            return sections;
        },
        educatorNavData() {
            const env = import.meta.env.MODE;
            const envPrefix = envMap[env.toLowerCase()] || '';
            return [
                {
                    name: '',
                    links: [
                        {
                            display: 'Discovery Education',
                            path: `https://app.${envPrefix}discoveryeducation.com`,
                            icon: 'caret-left',
                        },
                    ],
                },
                {
                    name: 'My Career App',
                    links: [
                        {
                            display: 'Home',
                            icon: 'dashboard',
                            path: '/',
                        },
                        {
                            display: 'Find a Volunteer',
                            icon: 'user-add',
                            path: '/volunteers',
                        },
                        {
                            display: 'View Volunteer Career Shares',
                            icon: 'split-group',
                            path: '/share-my-career',
                        },
                        {
                            display: 'Explore Organizations',
                            icon: 'buildings',
                            path: '/organizations',
                        },
                        {
                            display: 'Create a Request',
                            icon: 'mail-send',
                            path: '/request/v2/new',
                        },
                        {
                            display: 'How it Works',
                            icon: 'information',
                            path: '/how-it-works',
                        },
                    ],
                },
                {
                    name: 'My Connects & Requests',
                    links: [
                        {
                            display: 'Connect Hub',
                            icon: 'calendar',
                            path: '/connect-directory',
                        },
                        {
                            display: 'Messages',
                            icon: 'mail',
                            path: '/message',
                        },
                    ],
                },
                {
                    name: 'My Account',
                    links: [
                        {
                            display: 'My Discovery Education Profile',
                            icon: 'profile',
                            path: `https://app.${envPrefix}discoveryeducation.com/learn/profile/personalization`,
                            newTab: true,
                        },
                        {
                            display: 'Settings and Preferences',
                            icon: 'gear',
                            path: '/settings',
                        },
                    ],
                },
                {
                    name: 'Support',
                    links: [
                        {
                            display: this.$t('Help & Support'),
                            icon: 'help',
                            path: this.helpAndSupportLink,
                            newTab: true,
                        },
                    ],
                },
            ];
        },
        globalNavData() {
            return this.appStore.isEducator ? this.educatorNavData : this.nonEducatorNavData;
        },
        welcomeModalsEnabled() {
            const roleNotBlocked = !this.isBlockedRole && !this.appStore.isOrgAdmin;
            return !this.loading && roleNotBlocked;
        },
        bannerError() {
            return this.appStore?.appBannerError;
        },
    },
    methods: {
        checkWelcomeModalOnInitialLoad() {
            // Only run once per session
            if (this.welcomeModalChecked) {
                return;
            }

            this.welcomeModalChecked = true;
            const isHomepage = this.$route.path === '/' || this.$route.name === 'dashboard';

            if (isHomepage) {
                const welcomeStore = useWelcomeModalStore();
                welcomeStore.checkAndOpenIfNeeded();
            }
        },
        openWelcomeModal() {
            const welcomeStore = useWelcomeModalStore();
            welcomeStore.openModal();
        },
        openProfilePopover() {
            setTimeout(this.startPopoverTimeOut, 300);
        },
        startPopoverTimeOut() {
            if (this.$refs.accountPopover) {
                this.$refs.accountPopover.triggerAccountPopover();
                this.$nextTick(() => {
                    window.dispatchEvent(new Event('resize'));
                });
            }
        },
        injectLogo() {
            const oldLogo = document.querySelector('.nebula-product-bar__page-title-logo');

            if (!oldLogo) {
                return;
            }

            const CCLogo = document.createElement('img');
            CCLogo.setAttribute('src', '/img/career_connect_logo.svg');
            CCLogo.setAttribute('alt', 'career connect logo');
            CCLogo.classList.add('nebula-product-bar__page-title-logo');
            oldLogo.replaceWith(CCLogo);
        },
        replaceFooterText() {
            // The interpolation setting on localization seems to be incompatible with
            // how this app applies localization.
            // TODO resolve this, but for now update this way
            const lineEl = document.querySelector('.nebula-footer__bottom>p');

            if (!lineEl) {
                return;
            }

            const text = lineEl.textContent;
            const name = 'Discovery Education';

            if (text.includes(name)) {
                // bail if footer text already set
                return;
            }

            const now = new Date();
            const year = now.getFullYear();

            const replace = text.replace(/%\(name\)s/g, name).replace(/%\(year\)s/g, year);

            lineEl.textContent = replace;
        },
        handleToggleGlobalNav() {
            if (!this.$data.globalNavOpen) {
                this.$refs.globalNav.focusOnOpen();
            }

            this.$data.globalNavOpen = !this.$data.globalNavOpen;
        },
        makeSegmentScript(dateKey) {
            // Add analytics
            const analyticsScript = document.createElement('script');
            analyticsScript.onload = async () => {
                const config = await analytics.setDEAnalyticsConfig(this);
                // Only initialize if config is available (not null)
                if (config) {
                    window.DEAnalyticsInitData = config;
                    if (window.DEAnalytics) {
                        window.DEAnalytics.init();
                    }
                } else {
                    console.warn('[Analytics] Config not yet available, will retry when appMode is set');
                }
            };
            analyticsScript.setAttribute('src', `${analytics.DEScript}?key=${dateKey}`);
            document.head.appendChild(analyticsScript);
        },
        implementSegment() {
            if (this.appStore.isDeAdmin) {
                return;
            }

            // Guard: Don't initialize analytics until appMode is available
            if (!this.appMode) {
                console.log('[Analytics] Waiting for appMode to be available');
                return;
            }

            // Prevent double initialization
            if (this.analyticsInitialized) {
                return;
            }

            const dateKey = analytics.getDateKey();
            // check for existing script
            const script = document.querySelector(`head>script[src^='${analytics.DEScript}']`);

            // If analytics script not detected
            if (!script) {
                // Add analytics
                this.makeSegmentScript(dateKey);
                this.analyticsInitialized = true;
                return;
            }

            const currentSrc = script.getAttribute('src');
            const regex = /key=(\d*)$/g;
            const currentDateKey = regex.exec(currentSrc)[1];

            if (currentDateKey === dateKey) {
                // bail if script, with correct key is detected
                // no action needed
                this.analyticsInitialized = true;
                return;
            }

            // if script is detected with outdated key, replace
            script.setAttribute('src', `${analytics.DEScript}?key=${dateKey}`);
            this.analyticsInitialized = true;
        },
        implementPendo() {
            // pendo() skips (and reports false) until it has a role and an identified user,
            // so we keep trying on each appMode/loading change until it actually initializes.
            if (this.pendoInitialized) {
                return;
            }

            this.pendoInitialized = pendo();
        },
        async pollNotifications() {
            const payload = await this.createPayload();
            this.notificationStore.getNotifications(payload);
        },
    },
    async mounted() {
        // Add ID to profile avatar button for AccountPopover positioning
        await this.$nextTick();

        const profileButton = this.$refs.productBarContainer?.querySelector('.nebula-product-bar__avatar');
        if (profileButton) {
            profileButton.id = 'nebula-popover-trigger__popover-accounts';
            // Wait another tick to ensure ID is set before mounting AccountPopover
            await this.$nextTick();
            this.profileButtonReady = true;
        }
        // If button doesn't exist (prelogin state), profileButtonReady stays false
        // It will be set to true in the loading watcher when the button appears

        this.injectLogo();
        await this.$nextTick();
        this.injectLogo();

        this.replaceFooterText();

        this.footerLinks.push({
            link: this.helpAndSupportLink,
            target: '_blank',
            routerLink: false,
            text: this.$t('Help & Support'),
        });
    },
    watch: {
        bannerError(newError) {
            // Hold an internal representation of the error, so that if
            // the global state changes, we can persist it within the App until
            // the animation completes.
            clearTimeout(this.bannerErrorClearTimeout);

            if (newError) {
                this.displayedBannerError = newError;
                return;
            }

            // Keep the banner content mounted until the fade-out + collapse sequence
            // finishes (0.2s fade + 0.3s collapse) so it doesn't vanish early.
            this.bannerErrorClearTimeout = setTimeout(() => {
                this.displayedBannerError = null;
            }, 500);
        },
        // Watch for appMode changes to initialize analytics when ready
        appMode(mode) {
            if (mode && !this.loading && !this.analyticsInitialized) {
                console.log('[Analytics] appMode now available, initializing analytics');
                this.implementSegment();
            }

            if (mode && !this.loading) {
                this.implementPendo();
            }
        },
        async loading(loading) {
            if (!loading) {
                // Try to initialize analytics, but will defer if appMode not ready yet
                this.implementSegment();
                this.implementPendo();

                await this.$nextTick();

                // Ensure profile button has the correct ID for AccountPopover
                const profileButton = this.$refs.productBarContainer?.querySelector('.nebula-product-bar__avatar');
                if (profileButton && !profileButton.id) {
                    profileButton.id = 'nebula-popover-trigger__popover-accounts';
                    // Now that button has ID, allow AccountPopover to mount
                    await this.$nextTick();
                    this.profileButtonReady = true;
                }

                this.injectLogo();
                this.replaceFooterText();

                if (this.showNotifications) {
                    this.pollNotifications();
                    this.notificationPolling = setInterval(
                        () => {
                            this.pollNotifications();
                        },
                        1000 * 60 * 5,
                    );
                }

                // Load user favorites once on app initialization (only for educators)
                if (!this.favoritesLoaded && educatorRoles.includes(this.appMode)) {
                    this.favoritesLoaded = true;
                    try {
                        const payload = await this.createPayload();
                        await this.employeeStore.getFavorites(payload);
                    } catch (error) {
                        console.error('Failed to load favorites:', error);
                    }
                }
                this.checkWelcomeModalOnInitialLoad();
            }
        },
    },
    beforeUnmount() {
        clearInterval(this.notificationPolling);
        this.notificationPolling = null;
        clearTimeout(this.bannerErrorClearTimeout);
    },
};
</script>

<style lang="stylus">

:root {
    --nebula-input-max-width: 100%;
    --nebula-select-max-width: 100%;
}

html, body {
    height: 100%;
}

body {
    margin: 0;
    padding: 0;
}

#app {
    font-family: var(--nebula-font-family-display);
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    background-color: $nebula-color-platform-neutral-50;
    color: $nebula-color-platform-neutral-900;
    display: flex;
    flex-direction: column;
    height: 100%;
    margin: 0;
    padding: 0;
}

h1, h2, h3, h4, h5, h6 {
    header-base();
}

.careers-footer {
    z-index: 1;
}

.nebula-sidebar__section .nebula-button.router-link-exact-active {
    background: var(--nebula-button-background-hover);
    border-color: var(--nebula-button-border-color-hover);
    color: var(--nebula-button-text-color-hover);
}

.nebula-sidebar__toggle-nav-button {
    display: none;
}

.nebula-legend {
    display: block;
    font-size: $nebula-font-size-body-2;
    font-weight: 500;
    margin-bottom: $nebula-space-1x;
}

.nebula-fieldset {
    border-width: 0;
    margin-bottom: $nebula-space-3x;
    padding: 0;
    width: 100%;

    &:last-of-type {
        margin-bottom: 0;
    }
}

.nebula-select .nebula-select__input {
    background-color: $nebula-color-platform-white;
    border-radius: $nebula-border-radius-small;
}

.nebula-product-bar__toggle-search-button {
    display: none;
}

.product {
    &-bar {
        z-index: 1;
        position: relative;
    }
}

.product-container {
    display: flex;
    flex: 1;
    flex-direction: column;
}

.product-content {
    display: flex;
    flex: 1;
    flex-direction: column;
}

.careers-global-nav__overlay {
    position: fixed;
    height: 100%;
    top: 0;
    left: 0;
    width: 100%;
    z-index: 3;
    background: rgba(0, 0, 0, 0.5);
}

// We do a grid-based collapse as it supports dynamic heights and it much smoother
// because it does not have to make the inner ErrorBanner recalculate its height.
.error-banner-wrapper {
    display: grid;
    grid-template-rows: 0fr;
    transition: grid-template-rows 0.3s ease-in-out 0.2s;
    z-index: 2;

    // Opening: expand the row first (0-0.3s), then fade in (0.3s-0.5s)
    &.is-open {
        grid-template-rows: 1fr;
        transition: grid-template-rows 0.3s ease-in-out;

        .error-banner-wrapper__inner {
            opacity: 1;
            transition: opacity 0.2s ease-in-out 0.3s;
        }
    }

    &__inner {
        min-height: 0;
        overflow: hidden;
        opacity: 0;
        transition: opacity 0.2s ease-in-out;
    }
}

.error-banner-container {
    display: flex;
    justify-content: center;
    margin-top: $nebula-space-2x;
    background-color: transparent;

    .careers-block-message {
        max-width: none;
        width: 100%;
        @media (min-width: $nebula-breakpoints-tablet-portrait) {
            max-width: 800px;
        }
    }
}
</style>
